import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiCalendar, FiFilter, FiSearch, FiEye, FiDownload, FiX, FiPlus } from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AddOrderModal from '../../components/AddOrderModal';

const Billing = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [showCreateOrderModalAll, setShowCreateOrderModalAll] = useState(false);


  useEffect(() => {
    fetchTables();
  }, [showCreateOrderModal]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vendor/tables');
      if (response.data.success) {
        setTables(response.data.data || []);
      }
    } catch (error) {
      toast.error('Failed to fetch tables');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'occupied':
        return 'bg-green-100 text-green-800';
      case 'reserved':
        return 'bg-blue-100 text-blue-800';
      case 'available':
        return 'bg-gray-100 text-gray-800';
      case 'billing':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleGenerateBill = (table) => {
    setSelectedTable(table);
    // Open AddOrderModal with table data
    setShowCreateOrderModal(true);
  };

  const handleOpenCreateOrder = () => {
    setShowCreateOrderModalAll(true);
  };

  const handleFinalizeBilling = async () => {
    try {
      const response = await api.post(`/vendor/tables/${selectedTable._id}/generate-bill`);
      if (response.data.success) {
        toast.success('Bill generated successfully!');
        setShowBillingModal(false);
        fetchTables(); // Refresh tables
      }
    } catch (error) {
      toast.error('Failed to generate bill');
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading billing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <FaRupeeSign className="text-indigo-600" />
              Billing Management
            </h1>
            <p className="text-gray-600">Manage table billing and payments</p>
          </div>
          <button 
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
            onClick={handleOpenCreateOrder}
          >
            <FiPlus size={16} />
            Add Order
          </button>
        </div>
      </div>

      {/* Tables Grid - Simple Table Numbers Only */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-3">
        {tables.map((table) => (
          <div 
            key={table._id} 
            className={`bg-white rounded-lg shadow-sm border-2 p-3 sm:p-4 text-center cursor-pointer transition-all duration-200 hover:shadow-md ${
              table.status === 'occupied' ? 'border-green-500 bg-green-50' :
              table.status === 'reserved' ? 'border-blue-500 bg-blue-50' :
              table.status === 'billing' ? 'border-yellow-500 bg-yellow-50' :
              'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleGenerateBill(table)}
          >
            <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              {table.tableNumber}
            </div>
            <div className="text-xs text-gray-500 mb-1 sm:mb-2">
              Table
            </div>
            <div className={`text-xs font-medium ${
              table.status === 'occupied' ? 'text-green-700' :
              table.status === 'reserved' ? 'text-blue-700' :
              table.status === 'billing' ? 'text-yellow-700' :
              'text-gray-600'
            }`}>
              {table.status}
            </div>
            {table.customerName && (
              <div className="text-xs text-gray-600 mt-1 truncate">
                {table.customerName}
              </div>
            )}
            {table.currentOrder && (
              <div className="text-xs font-medium text-gray-900 mt-1">
                {formatCurrency(table.currentOrder.totalAmount)}
              </div>
            )}
          </div>
        ))}
      </div>

      {tables.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaRupeeSign className="text-gray-400 text-3xl" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No tables found</h3>
          <p className="text-gray-600">No tables available at the moment</p>
        </div>
      )}

      {/* Billing Modal */}
      {showBillingModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Billing Details</h2>
              <button
                onClick={() => setShowBillingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Table Number</p>
                <p className="font-medium text-gray-900">Table {selectedTable.tableNumber}</p>
              </div>

              {selectedTable.customerName && (
                <div>
                  <p className="text-sm text-gray-600">Customer Name</p>
                  <p className="font-medium text-gray-900">{selectedTable.customerName}</p>
                </div>
              )}

              {selectedTable.currentOrder && (
                <>
                  <div>
                    <p className="text-sm text-gray-600">Order Amount</p>
                    <p className="font-medium text-gray-900">{formatCurrency(selectedTable.currentOrder.totalAmount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Items Ordered</p>
                    <p className="font-medium text-gray-900">{selectedTable.currentOrder.items?.length || 0} items</p>
                  </div>
                </>
              )}

              {selectedTable.startTime && (
                <div>
                  <p className="text-sm text-gray-600">Start Time</p>
                  <p className="font-medium text-gray-900">{new Date(selectedTable.startTime).toLocaleString()}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              {selectedTable.status === 'occupied' && selectedTable.currentOrder && (
                <button
                  onClick={handleFinalizeBilling}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  Generate Bill
                </button>
              )}
              <button
                onClick={() => setShowBillingModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Order Modal - Same as Orders Page */}
      {
        showCreateOrderModal &&
        <AddOrderModal
          showCreateOrderModal={showCreateOrderModal}
          setShowCreateOrderModal={setShowCreateOrderModal}
          customerData={selectedTable ? {
            tableNumber: selectedTable?.tableNumber,
            customerName: selectedTable?.customerDetails?.fullName,
            customerMobile: selectedTable?.customerDetails?.mobileNumber
          } : {}}
          openFromPage="billing"
        />
      }

      {
        showCreateOrderModalAll &&
        <AddOrderModal
          showCreateOrderModal={showCreateOrderModalAll}
          setShowCreateOrderModal={setShowCreateOrderModalAll}
        />
      }
      

    </div>
  );
};

export default Billing;
